import { useEffect, useRef, useState } from 'react';
import {
  LANGUES_RELANCE, LIBELLE_LANGUE, LIBELLE_MOTIF_RELANCE, MOTIFS_RELANCE, normaliserSociete,
} from '@mosaik/core';
import { ModelesMail } from '../composants/ModelesMail';
import { ErreurApi } from '../api/client';
import { useMajReglages, useReglages, useSante } from '../api/hooks';
import {
  LIBELLE_CANAL,
  type CanalEnvoi, type ContactFournisseur, type LangueRelance, type ModeleMail,
} from '../api/types';
import { Chargement, Erreur } from '../composants/Etats';
import { useToasts } from '../composants/Toasts';

const LIBELLES_SEUILS: Array<[keyof Seuils, string, string]> = [
  ['envoiCritique', 'Reçue depuis', 'jour(s) → orange'],
  ['envoiBloquant', 'Reçue depuis', 'jour(s) sans être transmise → rouge'],
  ['relanceFournisseur', "Sans date d'expédition depuis", 'jour(s) → relancer'],
  ['enlevementDelai', 'Transmise à HIOLLE depuis', 'jour(s) → demander l’enlèvement (0 = dès la transmission)'],
  ['trackingRetard', 'Partie depuis', 'jour(s) sans tracking → à réclamer (0 = dès le départ)'],
  ['trackingBloquant', 'Partie depuis', 'jour(s) sans tracking → rouge (plus aucune surveillance)'],
  ['toleranceLivraison', 'Tolérance sur la livraison estimée', 'jour(s)'],
  ['suiviMuet', 'Suivi sans point depuis', 'jour(s) → à contrôler'],
  ['avisDelai', "Envoyer la demande d'avis", 'jour(s) après la livraison (reporté au jour ouvré suivant)'],
  ['avisJusqua', "Ne plus proposer d'avis au-delà de", 'jour(s) après la livraison'],
];

interface Seuils {
  envoiCritique: number; envoiBloquant: number;
  relanceFournisseur: number; enlevementDelai: number;
  trackingRetard: number; trackingBloquant: number; toleranceLivraison: number;
  suiviMuet: number; avisDelai: number; avisJusqua: number;
}

export function PageReglages() {
  const { data: reglages, isLoading, error, refetch } = useReglages();
  const { data: sante } = useSante();
  const majour = useMajReglages();
  const { ajouter } = useToasts();

  const [seuils, setSeuils] = useState<Seuils | null>(null);
  const [transit, setTransit] = useState<Record<string, number>>({});
  const [resolution, setResolution] = useState<Record<string, number>>({});
  const [objet, setObjet] = useState('');
  const [corps, setCorps] = useState('');
  const [annuaire, setAnnuaire] = useState<Record<string, ContactFournisseur>>({});
  const [modeles, setModeles] = useState<Record<LangueRelance, ModeleMail> | null>(null);
  const [nouvelle, setNouvelle] = useState('');
  // Un champ numérique se règle aussi aux flèches, qui ne donnent pas toujours le
  // focus (Safari, Firefox) : pas de blur, donc on enregistre à la saisie, une fois
  // les clics retombés, plutôt qu'à la sortie du champ.
  const minuteurs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  useEffect(() => {
    if (!reglages) return;
    setSeuils(reglages.seuils as Seuils);
    setTransit(reglages.transit);
    setResolution(reglages.resolutionLitige ?? {});
    setObjet(reglages.objetRelance);
    setCorps(reglages.corpsRelance);
    setAnnuaire(reglages.contactsFournisseur);
    setModeles(reglages.relanceFournisseur);
  }, [reglages]);

  if (isLoading && !reglages) return <section className="page"><Chargement /></section>;
  if (error) return <section className="page"><Erreur erreur={error} onReessayer={() => void refetch()} /></section>;
  if (!reglages || !seuils || !modeles) return null;

  async function enregistrer(champs: Record<string, unknown>, message: string) {
    try {
      await majour.mutateAsync(champs);
      ajouter(message);
    } catch (e) {
      ajouter(e instanceof ErreurApi ? e.message : "L'enregistrement a échoué.", 'bad');
    }
  }

  function enregistrerBientot(cle: string, champs: Record<string, unknown>, message: string) {
    clearTimeout(minuteurs.current[cle]);
    minuteurs.current[cle] = setTimeout(() => {
      delete minuteurs.current[cle];
      void enregistrer(champs, message);
    }, 600);
  }

  return (
    <section className="page" id="contenu">
      <div className="phead">
        <h1>Réglages</h1>
        <p>
          Les seuils qui font passer une commande en orange puis en rouge, et le paramétrage des
          automatismes. Ils vivent en base : le moteur de règles les reçoit en paramètre, rien n&apos;est
          codé en dur.
        </p>
      </div>

      <div className="grid2">
        <div className="card">
          <h2>Seuils d&apos;alerte</h2>
          <p className="hint">Appliqués immédiatement à toutes les commandes.</p>
          {LIBELLES_SEUILS.map(([cle, avant, apres]) => (
            <div className="rule" key={cle}>
              <label htmlFor={`s-${cle}`} style={{ flex: 1, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'inherit', fontSize: 13 }}>
                {avant}
              </label>
              <input
                id={`s-${cle}`} type="number" min={0} max={365} value={seuils[cle]}
                onChange={(e) => {
                  const valeur = Math.max(0, Number(e.target.value) || 0);
                  setSeuils({ ...seuils, [cle]: valeur });
                  enregistrerBientot(`seuil-${cle}`, { seuils: { [cle]: valeur } }, 'Seuil enregistré.');
                }}
              />
              <span style={{ flex: 1.1 }}>{apres}</span>
            </div>
          ))}
          <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
            Ces valeurs sont reprises du prototype : ce sont des arbitrages métier, pas des constantes
            techniques. Elles méritent d&apos;être confrontées aux délais réels. Toutes se comptent à
            partir d&apos;un fait de la commande — sa réception, son départ, sa livraison. Ce qui se
            compte à partir d&apos;une <em>relance</em> vit dans la carte ci-dessous.
          </p>
        </div>

        <div className="card">
          <h2>Délais de transit</h2>
          <p className="hint">
            Date d&apos;expédition + transit = livraison estimée. Cette estimation n&apos;est qu&apos;un
            filet de secours : la date renvoyée par le transporteur prime, et aucune estimation ne clôture
            une commande.
          </p>
          {Object.entries(transit).map(([t, jours]) => (
            <div className="rule" key={t}>
              <label htmlFor={`t-${t}`} style={{ flex: 1, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'inherit', fontSize: 13 }}>
                {t}
              </label>
              <input
                id={`t-${t}`} type="number" min={0} max={60} value={jours}
                onChange={(e) => {
                  const suivant = { ...transit, [t]: Math.max(0, Number(e.target.value) || 0) };
                  setTransit(suivant);
                  enregistrerBientot('transit', { transit: suivant }, 'Délai de transit enregistré.');
                }}
              />
              <span>j</span>
            </div>
          ))}
        </div>

        <div className="card">
          <h2>Délais de résolution des litiges</h2>
          <p className="hint">
            Le temps qu&apos;on laisse à l&apos;autre partie pour répondre à une relance. Pendant ce
            délai la commande ne réclame rien et vit dans l&apos;onglet « Problèmes », avec son
            échéance. Le terme passé, elle repart d&apos;elle-même : « En cours » si ce qu&apos;on
            réclamait est arrivé, « À faire » sinon.
          </p>
          {MOTIFS_RELANCE.map((motif) => (
            <div className="rule" key={motif}>
              <label htmlFor={`r-${motif}`} style={{ flex: 1, fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: 'inherit', fontSize: 13 }}>
                Relance pour {LIBELLE_MOTIF_RELANCE[motif]}
              </label>
              <input
                id={`r-${motif}`} type="number" min={0} max={365}
                value={resolution[motif] ?? 0}
                onChange={(e) => {
                  const suivant = { ...resolution, [motif]: Math.max(0, Number(e.target.value) || 0) };
                  setResolution(suivant);
                  enregistrerBientot(
                    'resolution', { resolutionLitige: suivant }, 'Délai de résolution enregistré.',
                  );
                }}
              />
              <span>j</span>
            </div>
          ))}
          <p className="hint" style={{ marginTop: 10, marginBottom: 0 }}>
            À 0, une relance n&apos;ouvre aucun sursis : la commande réclame de nouveau dès le
            lendemain, et ne passe jamais par « Problèmes ».
          </p>
        </div>

        <div className="card">
          <h2>Intégrations</h2>

          {/* Ce que l'application fait de SES mails : la carte détaille ensuite
              les trois canaux, qui passent tous par la même boîte. */}
          <div
            className="fieldset"
            style={{
              borderLeft: `4px solid ${sante?.brouillons.boiteRefusee ? 'var(--rouge)' : 'var(--vert)'}`,
              background: sante?.brouillons.boiteRefusee ? 'var(--brick-soft)' : 'var(--pine-soft)',
              marginBottom: 12,
            }}
          >
            <h3 style={{ color: sante?.brouillons.boiteRefusee ? 'var(--brick)' : 'var(--pine)' }}>
              {sante?.brouillons.boiteRefusee
                ? "Boîte d'envoi refusée"
                : `Mails envoyés depuis ${sante?.brouillons.boite ?? '—'}`}
            </h3>
            <p
              style={{
                fontSize: 12.5, margin: 0,
                color: sante?.brouillons.boiteRefusee ? 'var(--brick)' : 'var(--pine)',
              }}
            >
              {sante?.brouillons.boiteRefusee ?? sante?.brouillons.regle
                ?? 'État inconnu : l\'API ne répond pas.'}
            </p>
          </div>

          <p className="hint">
            Un canal par événement du flux. Les trois mails sont composés par l&apos;application,
            relus à l&apos;écran, puis <strong>envoyés directement</strong> depuis Outlook au clic
            sur « Envoyer » — la fenêtre de l&apos;application est la seule relecture, rien ne part
            tout seul, sauf les mails d&apos;expédition et de suivi déclenchés par une découverte
            sur le portail Fortemix, qui partent sans relecture. Les secrets vivent dans
            l&apos;environnement du serveur, jamais en base ni dans le navigateur. Cet écran ne fait
            que dire ce qui est actif.
          </p>

          {(['fournisseur', 'expedition', 'tracking'] as CanalEnvoi[]).map((canal) => (
            <EtatLigne
              key={canal}
              titre={`${LIBELLE_CANAL[canal]} (envoi Outlook)`}
              actif={sante?.integrations.canaux?.[canal]?.disponible ?? false}
              simule={sante?.integrations.canaux?.[canal]?.simule ?? false}
              raison={sante?.integrations.canaux?.[canal]?.raison ?? null}
              variable="AZURE_TENANT_ID + AZURE_CLIENT_ID + AZURE_CLIENT_SECRET + GRAPH_SENDER_UPN"
            />
          ))}
          <EtatLigne
            titre="Fiche client HubSpot"
            actif={sante?.integrations.hubspot.disponible ?? false}
            simule={sante?.integrations.hubspot.simule ?? false}
            raison={sante?.integrations.hubspot.raison ?? null}
            variable="HUBSPOT_TOKEN + HUBSPOT_PORTAL_ID"
          />

          {/* Le relevé se fait transporteur par transporteur, et tous ne sont pas
              logés à la même enseigne : cette liste dit lequel marche, et sinon
              pourquoi. L'écran annonçait autrefois un horaire sans que rien ne
              relève quoi que ce soit. */}
          <div className="rule" style={{ marginTop: 12, alignItems: 'baseline' }}>
            <span style={{ flex: 1 }}>Relevé du suivi transporteur</span>
            <span className="tag">
              {sante?.suivi.automatique
                ? horairesSuivi(sante.suivi.heures)
                : 'automatique désactivé'}
            </span>
          </div>
          {Object.entries(sante?.suivi.transporteurs ?? {}).map(([transporteur, etat]) => (
            <EtatLigne
              key={transporteur}
              titre={transporteur}
              actif={etat.disponible}
              simule={etat.simule}
              raison={etat.raison}
              variable={null}
            />
          ))}
          <p className="hint" style={{ marginBottom: 0 }}>
            Les transporteurs sont interrogés <strong>directement</strong>, chacun par sa propre voie.
            Le relevé remplit le statut et sa date, et bascule la commande en « livrée » uniquement
            sur un statut de livraison confirmé — jamais sur une date estimée. Le bouton
            « Relever maintenant » de la fiche fait le même geste, à la demande.
          </p>
        </div>

        <ModelesMail reglages={reglages} onEnregistrer={(champs, message) => void enregistrer(champs, message)} />

        <div className="card">
          <h2>Modèle de relance transporteur</h2>
          <p className="hint">
            Proposé quand une livraison est bloquée ou en retard. Quand plusieurs colis d&apos;une
            commande sont bloqués, une seule relance les nomme tous : <code>{'{{tracking}}'}</code>{' '}
            les énumère et <code>{'{{statut}}'}</code> devient la liste détaillée, un colis par
            ligne — que <code>{'{{colis}}'}</code> permet aussi de placer ailleurs.
          </p>

          <div className="field">
            <label htmlFor="r-obj">Objet</label>
            <input
              id="r-obj" value={objet} onChange={(e) => setObjet(e.target.value)}
              onBlur={() => void enregistrer({ objetRelance: objet }, 'Modèle enregistré.')}
            />
          </div>
          <div className="field">
            <label htmlFor="r-corps">Message</label>
            <textarea
              id="r-corps" rows={12} value={corps} onChange={(e) => setCorps(e.target.value)}
              onBlur={() => void enregistrer({ corpsRelance: corps }, 'Modèle enregistré.')}
            />
          </div>

          {/* Il n'y a plus d'adresses de transporteur à saisir ici : les
              transporteurs n'ont pas de service litige, et la relance part chez
              le fournisseur. Un champ qu'on remplit sans que rien ne le lise
              serait pire que pas de champ du tout. */}
          <p className="hint" style={{ marginBottom: 0 }}>
            La relance est adressée <strong>au fournisseur</strong>, aux mêmes personnes que le mail
            de commande : les transporteurs n&apos;ont pas de service litige, et c&apos;est le
            fournisseur qui est leur donneur d&apos;ordre. Les adresses viennent donc de
            l&apos;annuaire fournisseurs ci-dessous.
          </p>
        </div>
        <div className="card">
          <h2>Relance fournisseur</h2>
          <p className="hint">
            Proposée dès qu&apos;une commande transmise reste sans date d&apos;expédition. La langue et
            les adresses viennent de l&apos;annuaire ci-dessous ; le mail reste modifiable dans la
            fenêtre de relance, puis part depuis Outlook au clic sur « Envoyer ».
          </p>

          {LANGUES_RELANCE.map((langue) => (
            <div key={langue} style={{ marginBottom: 14 }}>
              <h3 style={{ margin: '10px 0 8px' }}>Modèle {LIBELLE_LANGUE[langue].toLowerCase()}</h3>
              <div className="field">
                <label htmlFor={`rf-obj-${langue}`}>Objet</label>
                <input
                  id={`rf-obj-${langue}`} value={modeles[langue].objet}
                  onChange={(e) => setModeles({
                    ...modeles, [langue]: { ...modeles[langue], objet: e.target.value },
                  })}
                  onBlur={() => void enregistrer(
                    { relanceFournisseur: { [langue]: { objet: modeles[langue].objet } } },
                    'Modèle enregistré.',
                  )}
                />
              </div>
              <div className="field">
                <label htmlFor={`rf-corps-${langue}`}>Message</label>
                <textarea
                  id={`rf-corps-${langue}`} rows={14} value={modeles[langue].corps}
                  onChange={(e) => setModeles({
                    ...modeles, [langue]: { ...modeles[langue], corps: e.target.value },
                  })}
                  onBlur={() => void enregistrer(
                    { relanceFournisseur: { [langue]: { corps: modeles[langue].corps } } },
                    'Modèle enregistré.',
                  )}
                />
              </div>
            </div>
          ))}

          <p className="hint">
            Variables remplies depuis la commande : <span className="mono">{'{{ref}}'}</span>,{' '}
            <span className="mono">{'{{fournisseur}}'}</span>,{' '}
            <span className="mono">{'{{transmise}}'}</span> (date d&apos;envoi),{' '}
            <span className="mono">{'{{attente}}'}</span> (jours sans réponse),{' '}
            <span className="mono">{'{{articles}}'}</span> (lignes du fournisseur),{' '}
            <span className="mono">{'{{adresse}}'}</span> (bloc de livraison),{' '}
            <span className="mono">{'{{reception}}'}</span>,{' '}
            <span className="mono">{'{{client}}'}</span>,{' '}
            <span className="mono">{'{{ville}}'}</span>,{' '}
            <span className="mono">{'{{note}}'}</span>. Vider un champ le ramène au modèle
            d&apos;origine.
          </p>
          <p className="hint">
            Le modèle d&apos;origine ne reprend <strong>ni les lignes produit ni l&apos;adresse</strong> :
            on réclame une date à quelqu&apos;un qui a déjà la commande. Les deux variables restent
            disponibles si vous voulez les remettre. Une commande que le fournisseur ne retrouve pas
            ne passe pas par ici — elle se <em>repasse</em>, depuis la fenêtre de commande.
          </p>

          <h3 style={{ margin: '14px 0 8px' }}>Annuaire fournisseurs</h3>
          <p className="hint">
            À qui écrire et dans quelle langue. Plusieurs adresses se séparent par des virgules.
            Une société absente d&apos;ici n&apos;empêche pas la relance : la fenêtre s&apos;ouvre
            sans destinataire plutôt que d&apos;en inventer un.
          </p>

          {Object.entries(annuaire).sort(([a], [b]) => a.localeCompare(b)).map(([societe, fiche]) => (
            <div className="field" key={societe}>
              <label htmlFor={`fr-${societe}`}>{societe}</label>
              <div className="rule" style={{ marginTop: 0 }}>
                <input
                  id={`fr-${societe}`} type="text" value={fiche.email}
                  placeholder="adresses, séparées par des virgules"
                  onChange={(e) => setAnnuaire({
                    ...annuaire, [societe]: { ...fiche, email: e.target.value },
                  })}
                  onBlur={() => void enregistrer({ contactsFournisseur: annuaire }, 'Annuaire enregistré.')}
                />
                <select
                  aria-label={`Langue de ${societe}`} value={fiche.langue}
                  onChange={(e) => {
                    const maj = { ...annuaire, [societe]: { ...fiche, langue: e.target.value as LangueRelance } };
                    setAnnuaire(maj);
                    void enregistrer({ contactsFournisseur: maj }, 'Annuaire enregistré.');
                  }}
                >
                  {LANGUES_RELANCE.map((l) => <option key={l} value={l}>{LIBELLE_LANGUE[l]}</option>)}
                </select>
              </div>
            </div>
          ))}

          <div className="rule" style={{ marginTop: 10 }}>
            <input
              aria-label="Nouvelle société" type="text" value={nouvelle}
              placeholder="ajouter une société…"
              onChange={(e) => setNouvelle(e.target.value)}
            />
            <button
              type="button" className="btn sm" disabled={!normaliserSociete(nouvelle)}
              onClick={() => {
                const cle = normaliserSociete(nouvelle);
                if (!cle || annuaire[cle]) { setNouvelle(''); return; }
                const maj = { ...annuaire, [cle]: { email: '', langue: 'fr' as LangueRelance } };
                setAnnuaire(maj);
                setNouvelle('');
                void enregistrer({ contactsFournisseur: maj }, `${cle} ajoutée à l'annuaire.`);
              }}
            >
              Ajouter
            </button>
          </div>
          <p className="hint" style={{ marginBottom: 0 }}>
            Le nom doit être celui du référentiel produits — c&apos;est lui qui figure sur les
            commandes, et c&apos;est par lui que l&apos;adresse est retrouvée.
          </p>
        </div>
      </div>

      <p className="sub" style={{ marginTop: 16 }}>
        Dernière modification : {reglages.modifiePar ?? '—'}
        {reglages.updatedAt ? ` · ${new Date(reglages.updatedAt).toLocaleString('fr-FR')}` : ''}
      </p>
    </section>
  );
}

/** `[8, 13]` → « 08:00 et 13:00 ». Les heures viennent du serveur, pas d'un texte libre. */
function horairesSuivi(heures: number[]): string {
  const formatees = heures.map((h) => `${String(h).padStart(2, '0')}:00`);
  if (formatees.length <= 1) return formatees[0] ?? 'jamais';
  return `${formatees.slice(0, -1).join(', ')} et ${formatees[formatees.length - 1]}`;
}

function EtatLigne({
  titre, actif, simule, raison, variable,
}: {
  titre: string; actif: boolean; simule: boolean; raison: string | null;
  /** `null` quand la raison se suffit à elle-même — le relevé transporteur la nomme déjà. */
  variable: string | null;
}) {
  const ton = actif && !simule ? 'pine' : simule ? 'ocre' : 'brick';
  const libelle = actif && !simule ? 'active' : simule ? 'mode local' : 'inactive';

  return (
    <div style={{ marginBottom: 12 }}>
      <div className="rule" style={{ marginBottom: 4 }}>
        <span style={{ flex: 1 }}>{titre}</span>
        <span className={`tag ${ton}`}>{libelle}</span>
      </div>
      <p className="hint" style={{ margin: 0 }}>
        {raison ?? 'Configurée et opérationnelle.'}
        {!actif && variable && <> Variable attendue : <span className="mono">{variable}</span>.</>}
      </p>
    </div>
  );
}
